import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { gymAPI } from '../lib/api';
import { UnitSwitch, fromKg, plain, otherUnit } from './gymUnits';

/**
 * Gym progress, for a laptop: strength over time per exercise, from every
 * finished workout. An exercise is the same machine wherever it appears ("Pec
 * fly" in any session), so its history follows it across sessions.
 *
 * Two charts, never one with two scales: the heaviest set (or the estimated
 * one-rep max) over time as a line per person, and the volume of each workout
 * (Σ reps × kg) as columns. Simon and Rachel keep the same colour everywhere
 * (validated as a pair on both gym surfaces); text never wears it.
 */

// Categorical slots 1 and 2, dark steps; checked against #141414 and the Rabel berry.
const PERSON_COLOURS = ['#3987e5', '#d95926'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const day = (iso) => { const d = new Date(iso); return `${d.getDate()} ${MONTHS[d.getMonth()]}`; };
/** A kg value as shown, in the unit in force: "60 kg", "132.3 lb". */
const kg = (v, unit = 'kg') => (v == null ? '-' : `${plain(Math.round(fromKg(v, unit) * 10) / 10)} ${unit}`);
const signed = (v, unit = '') => (v == null ? '-' : `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(Math.round(v * 10) / 10)}${unit}`);
const setsText = (sets, unit = 'kg') => sets.map(s => `${s.reps ?? '-'}${s.weight != null ? `×${plain(fromKg(s.weight, unit))}` : ''}`).join(' · ');

export default function GymProgress() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [who, setWho] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [metric, setMetric] = useState('top');
  // Each exercise shows in its machine's unit; switching here is for viewing only.
  const [viewUnit, setViewUnit] = useState({});
  const unitFor = (e) => viewUnit[e.key] || e.unit || 'kg';

  useEffect(() => {
    gymAPI.progress().then(r => setData(r.data)).catch(() => setFailed(true));
  }, []);

  const people = data?.people || [];
  const colourOf = useMemo(() => Object.fromEntries(people.map((p, i) => [p.id, PERSON_COLOURS[i % PERSON_COLOURS.length]])), [people]);
  const shown = (points) => points.filter(p => who === 'all' || p.userId === who);

  const exercises = useMemo(() => (data?.exercises || [])
    .map(e => ({ ...e, visible: shown(e.points) }))
    .filter(e => e.visible.length && e.name.toLowerCase().includes(query.trim().toLowerCase())),
  [data, who, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = exercises.find(e => e.key === selected) || exercises[0] || null;

  if (failed) return <p className="pt-10 text-gray-500">Could not load progress.</p>;
  if (!data) return <p className="pt-10 text-gray-500">Loading…</p>;

  return (
    <div className="pt-4">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Link to="/gym" className="text-green-400 text-2xl leading-none" aria-label="Back">‹</Link>
        <h1 className="text-2xl font-bold flex-1">Progress</h1>
        <div className="flex rounded-full border border-[#2a2a2a] overflow-hidden text-sm">
          {[{ id: 'all', name: 'Both' }, ...people].map(p => (
            <button key={p.id} onClick={() => setWho(p.id)} className={`px-4 py-1.5 flex items-center gap-2 ${who === p.id ? 'bg-[#1f1f1f] text-white' : 'text-gray-400'}`}>
              {p.id !== 'all' && <span className="w-2.5 h-2.5 rounded-full" style={{ background: colourOf[p.id] }} />}
              {p.name}
            </button>
          ))}
        </div>
      </div>

      {!data.exercises.length && <p className="text-gray-500">No finished workouts yet. Finish one in the gym and it shows up here.</p>}

      {data.exercises.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-5">
          <aside className="space-y-2">
            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find an exercise" className="w-full bg-[#141414] border border-[#2a2a2a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-green-500" />
            <div className="space-y-1 md:max-h-[75vh] md:overflow-y-auto pr-1">
              {exercises.map(e => (
                <button key={e.key} onClick={() => setSelected(e.key)} className={`w-full text-left rounded-xl px-3 py-2 border ${current?.key === e.key ? 'bg-[#1a1a1a] border-green-800' : 'bg-[#141414] border-[#232323] hover:border-[#333]'}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold flex-1 truncate">{e.name}</span>
                    <Sparkline points={e.visible} colourOf={colourOf} />
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {people.filter(p => who === 'all' || p.id === who).map(p => {
                      const mine = e.points.filter(x => x.userId === p.id && x.top != null);
                      if (!mine.length) return null;
                      return <span key={p.id} className="mr-3">{p.name} {signed(fromKg(mine.at(-1).top - mine[0].top, unitFor(e)), ` ${unitFor(e)}`)}</span>;
                    })}
                    <span>{e.visible.length} workout{e.visible.length === 1 ? '' : 's'}</span>
                  </div>
                </button>
              ))}
              {!exercises.length && <p className="text-sm text-gray-500 px-1">Nothing matches.</p>}
            </div>
          </aside>

          {current && (
            <section className="min-w-0 space-y-5">
              <div className="flex items-baseline gap-3">
                <h2 className="text-xl font-semibold">{current.name}</h2>
                <span className="text-xs text-gray-500">in <UnitSwitch unit={unitFor(current)} onToggle={() => setViewUnit(v => ({ ...v, [current.key]: otherUnit(unitFor(current)) }))} className="!text-xs" /></span>
              </div>
              <Tiles exercise={current} people={people.filter(p => who === 'all' || p.id === who)} colourOf={colourOf} unit={unitFor(current)} />

              <Panel
                title={metric === 'top' ? 'Heaviest set' : 'Estimated one-rep max'}
                hint={metric === 'top' ? 'The heaviest weight lifted in a set, each workout.' : 'Epley: weight × (1 + reps ÷ 30), best set each workout. Lets a heavier set of fewer reps and a lighter set of more reps be compared.'}
                right={
                  <div className="flex rounded-full border border-[#2a2a2a] overflow-hidden text-xs">
                    {[['top', 'Heaviest set'], ['e1rm', 'Est. 1-rep max']].map(([k, label]) => (
                      <button key={k} onClick={() => setMetric(k)} className={`px-3 py-1 ${metric === k ? 'bg-[#1f1f1f] text-white' : 'text-gray-400'}`}>{label}</button>
                    ))}
                  </div>
                }
              >
                <LineChart points={current.visible} people={people} colourOf={colourOf} value={p => fromKg(p[metric], unitFor(current))} unit={unitFor(current)} />
              </Panel>

              <Panel title="Volume per workout" hint="Every set added up: reps × kg. More sets, more reps or more weight all count.">
                <ColumnChart points={current.visible} people={people} colourOf={colourOf} unit={unitFor(current)} />
              </Panel>

              <Panel title="Every workout">
                <LogTable points={current.visible} colourOf={colourOf} unit={unitFor(current)} />
              </Panel>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ title, hint, right, children }) {
  return (
    <div className="rounded-2xl bg-[#141414] border border-[#232323] p-4">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{title}</div>
          {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

/** The headline numbers, per person shown. */
function Tiles({ exercise, people, colourOf, unit }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {people.map(p => {
        const mine = exercise.points.filter(x => x.userId === p.id);
        if (!mine.length) return null;
        const tops = mine.filter(x => x.top != null);
        const best = tops.length ? Math.max(...tops.map(x => x.top)) : null;
        const e1 = mine.filter(x => x.e1rm != null);
        const bestE1 = e1.length ? Math.max(...e1.map(x => x.e1rm)) : null;
        const last = mine.at(-1);
        const firstTop = tops[0]?.top;
        const gain = tops.length > 1 ? tops.at(-1).top - firstTop : null;
        return (
          <div key={p.id} className="rounded-2xl bg-[#141414] border border-[#232323] p-4">
            <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: colourOf[p.id] }} />
              {p.name} · {mine.length} workout{mine.length === 1 ? '' : 's'} since {day(mine[0].date)}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              <Stat label="Best set" value={kg(best, unit)} />
              <Stat label="Est. 1RM" value={kg(bestE1, unit)} />
              <Stat label="Since first" value={gain == null ? '-' : signed(fromKg(gain, unit), ` ${unit}`)} sub={gain != null && firstTop ? `${signed((gain / firstTop) * 100, '%')}` : null} />
              <Stat label="Last volume" value={`${Math.round(fromKg(last.volume, unit))}`} sub={`${unit} · ${day(last.date)}`} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className="text-xl font-semibold tabular-nums text-white truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 truncate">{sub}</div>}
    </div>
  );
}

// --- Charts -----------------------------------------------------------------------------

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(600);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(260, e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Round ticks that cover [lo, hi]. */
function niceTicks(lo, hi, count = 4) {
  if (lo === hi) { lo -= 1; hi += 1; }
  const raw = (hi - lo) / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(m => m * mag).find(s => s >= raw) || raw;
  const start = Math.floor(lo / step) * step;
  const ticks = [];
  for (let v = start; v <= hi + step * 0.001; v += step) ticks.push(Math.round(v * 100) / 100);
  if (ticks.at(-1) < hi) ticks.push(Math.round((ticks.at(-1) + step) * 100) / 100);
  return ticks;
}

function timeTicks(t0, t1, max = 6) {
  const out = [];
  const days = (t1 - t0) / 864e5;
  const stepDays = days <= 10 ? 1 : days <= 45 ? 7 : days <= 120 ? 14 : 30;
  const start = new Date(t0);
  start.setHours(0, 0, 0, 0);
  for (let t = start.getTime(); t <= t1; t += stepDays * 864e5) if (t >= t0) out.push(t);
  const skip = Math.ceil(out.length / max);
  return out.filter((_, i) => i % skip === 0);
}

const M = { top: 24, right: 64, bottom: 26, left: 44 };
const HEIGHT = 240;

/** One line per person; crosshair and readout on hover. Nothing to draw with fewer than one point. */
function LineChart({ points, people, colourOf, value, unit }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const series = people
    .map(p => ({ person: p, pts: points.filter(x => x.userId === p.id && value(x) != null).map(x => ({ ...x, t: new Date(x.date).getTime(), v: value(x) })) }))
    .filter(s => s.pts.length);
  if (!series.length) return <p className="text-sm text-gray-500">No weights logged for this yet (bodyweight sets count reps only).</p>;

  const all = series.flatMap(s => s.pts);
  let t0 = Math.min(...all.map(p => p.t));
  let t1 = Math.max(...all.map(p => p.t));
  if (t0 === t1) { t0 -= 3 * 864e5; t1 += 3 * 864e5; }
  const vs = all.map(p => p.v);
  const pad = Math.max(2.5, (Math.max(...vs) - Math.min(...vs)) * 0.15);
  const yTicks = niceTicks(Math.max(0, Math.min(...vs) - pad), Math.max(...vs) + pad);
  const [y0, y1] = [yTicks[0], yTicks.at(-1)];
  const iw = width - M.left - M.right;
  const ih = HEIGHT - M.top - M.bottom;
  const x = (t) => M.left + ((t - t0) / (t1 - t0)) * iw;
  const y = (v) => M.top + ih - ((v - y0) / (y1 - y0)) * ih;

  const times = [...new Set(all.map(p => p.t))].sort((a, b) => a - b);
  function onMove(e) {
    const box = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - box.left) / box.width) * width;
    const t = times.reduce((best, tt) => (Math.abs(x(tt) - px) < Math.abs(x(best) - px) ? tt : best), times[0]);
    setHover(t);
  }
  const at = hover == null ? [] : series.map(s => ({ s, p: s.pts.find(p => p.t === hover) })).filter(r => r.p);

  return (
    <div ref={ref} className="relative">
      {series.length > 1 && <Legend series={series.map(s => s.person)} colourOf={colourOf} />}
      <svg width={width} height={HEIGHT} onMouseMove={onMove} onMouseLeave={() => setHover(null)} className="block">
        {yTicks.map(v => (
          <g key={v}>
            <line x1={M.left} x2={width - M.right} y1={y(v)} y2={y(v)} stroke="#262626" strokeWidth="1" />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#8a8a8a">{v}</text>
          </g>
        ))}
        {timeTicks(t0, t1).map(t => (
          <text key={t} x={x(t)} y={HEIGHT - 6} textAnchor="middle" fontSize="11" fill="#8a8a8a">{day(new Date(t).toISOString())}</text>
        ))}
        <text x={M.left - 8} y={10} textAnchor="end" fontSize="10" fill="#6b6b6b">{unit}</text>
        {hover != null && <line x1={x(hover)} x2={x(hover)} y1={M.top} y2={M.top + ih} stroke="#4a4a4a" strokeWidth="1" />}
        {series.map(s => (
          <g key={s.person.id}>
            {s.pts.length > 1 && (
              <polyline fill="none" stroke={colourOf[s.person.id]} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" points={s.pts.map(p => `${x(p.t)},${y(p.v)}`).join(' ')} />
            )}
            {s.pts.map(p => (
              <circle key={p.runId} cx={x(p.t)} cy={y(p.v)} r={p.t === hover ? 6 : 4} fill={colourOf[s.person.id]} stroke="#141414" strokeWidth="2" />
            ))}
            {/* Direct label at the line's end: the latest value, in text ink. */}
            <text x={x(s.pts.at(-1).t) + 9} y={y(s.pts.at(-1).v) + 4} fontSize="12" fill="#d4d4d4">{s.pts.at(-1).v}</text>
          </g>
        ))}
      </svg>
      {at.length > 0 && (
        <Tooltip left={x(hover)} width={width}>
          <div className="text-gray-400 mb-1">{day(new Date(hover).toISOString())}</div>
          {at.map(({ s, p }) => (
            <div key={s.person.id} className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: colourOf[s.person.id] }} />
              <span className="text-gray-300">{s.person.name}</span>
              <span className="ml-auto tabular-nums text-white">{plain(p.v)} {unit}</span>
            </div>
          ))}
          {at.length === 1 && <div className="text-gray-500 mt-1">{setsText(at[0].p.sets, unit)}</div>}
        </Tooltip>
      )}
    </div>
  );
}

/** Volume per workout: a column per workout in date order, coloured by who did it. */
function ColumnChart({ points, people, colourOf, unit }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const cols = points.filter(p => p.volume > 0).map(p => ({ ...p, volume: fromKg(p.volume, unit) }));
  if (!cols.length) return <p className="text-sm text-gray-500">No weighted sets yet.</p>;
  const yTicks = niceTicks(0, Math.max(...cols.map(c => c.volume)));
  const top = yTicks.at(-1);
  const iw = width - M.left - M.right;
  const ih = HEIGHT - M.top - M.bottom;
  const band = iw / cols.length;
  const bw = Math.min(24, Math.max(4, band - 4));
  const y = (v) => M.top + ih - (v / top) * ih;
  const shownPeople = people.filter(p => cols.some(c => c.userId === p.id));
  const labelEvery = Math.ceil(cols.length / 8);

  return (
    <div ref={ref} className="relative">
      {shownPeople.length > 1 && <Legend series={shownPeople} colourOf={colourOf} />}
      <svg width={width} height={HEIGHT} className="block" onMouseLeave={() => setHover(null)}>
        {yTicks.map(v => (
          <g key={v}>
            <line x1={M.left} x2={width - M.right} y1={y(v)} y2={y(v)} stroke="#262626" strokeWidth="1" />
            <text x={M.left - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#8a8a8a">{v >= 1000 ? `${v / 1000}k` : v}</text>
          </g>
        ))}
        <text x={M.left - 8} y={10} textAnchor="end" fontSize="10" fill="#6b6b6b">{unit}</text>
        {cols.map((c, i) => {
          const cx = M.left + band * i + band / 2;
          const h = Math.max(1, M.top + ih - y(c.volume));
          const yTop = M.top + ih - h;
          const r = Math.min(4, bw / 2, h);
          // Rounded at the data end, square on the baseline.
          const d = `M${cx - bw / 2},${M.top + ih} V${yTop + r} Q${cx - bw / 2},${yTop} ${cx - bw / 2 + r},${yTop} H${cx + bw / 2 - r} Q${cx + bw / 2},${yTop} ${cx + bw / 2},${yTop + r} V${M.top + ih} Z`;
          return (
            <g key={c.runId} onMouseEnter={() => setHover(i)}>
              <rect x={cx - band / 2} y={M.top} width={band} height={ih} fill="transparent" />
              <path d={d} fill={colourOf[c.userId]} opacity={hover == null || hover === i ? 1 : 0.55} />
              {i % labelEvery === 0 && <text x={cx} y={HEIGHT - 6} textAnchor="middle" fontSize="11" fill="#8a8a8a">{day(c.date)}</text>}
            </g>
          );
        })}
      </svg>
      {hover != null && (
        <Tooltip left={M.left + band * hover + band / 2} width={width}>
          <div className="text-gray-400 mb-1">{day(cols[hover].date)} · {cols[hover].who}</div>
          <div className="tabular-nums text-white">{Math.round(cols[hover].volume)} {unit} volume</div>
          <div className="text-gray-500">{cols[hover].reps} reps: {setsText(cols[hover].sets, unit)}</div>
        </Tooltip>
      )}
    </div>
  );
}

function Legend({ series, colourOf }) {
  return (
    <div className="flex gap-4 text-xs text-gray-400 mb-1">
      {series.map(p => (
        <span key={p.id} className="flex items-center gap-1.5"><span className="w-3 h-0.5 rounded" style={{ background: colourOf[p.id] }} />{p.name}</span>
      ))}
    </div>
  );
}

function Tooltip({ left, width, children }) {
  const flip = left > width - 200;
  return (
    <div className="pointer-events-none absolute top-6 z-10 rounded-lg bg-[#0d0d0d] border border-[#333] px-3 py-2 text-xs shadow-lg min-w-[150px]" style={flip ? { right: width - left + 12 } : { left: left + 12 }}>
      {children}
    </div>
  );
}

/** A tiny trend in the list: heaviest set over time, one line per person shown. */
function Sparkline({ points, colourOf }) {
  const w = 64;
  const h = 20;
  const byPerson = {};
  points.filter(p => p.top != null).forEach(p => { (byPerson[p.userId] ||= []).push(p); });
  const all = Object.values(byPerson).flat();
  if (all.length < 2) return null;
  const ts = all.map(p => new Date(p.date).getTime());
  const vs = all.map(p => p.top);
  const [t0, t1, v0, v1] = [Math.min(...ts), Math.max(...ts), Math.min(...vs), Math.max(...vs)];
  const x = (t) => (t1 === t0 ? w / 2 : ((t - t0) / (t1 - t0)) * (w - 4) + 2);
  const y = (v) => (v1 === v0 ? h / 2 : h - 2 - ((v - v0) / (v1 - v0)) * (h - 4));
  return (
    <svg width={w} height={h} aria-hidden>
      {Object.entries(byPerson).map(([id, pts]) => pts.length > 1 && (
        <polyline key={id} fill="none" stroke={colourOf[id]} strokeWidth="1.5" strokeLinejoin="round" points={pts.map(p => `${x(new Date(p.date).getTime())},${y(p.top)}`).join(' ')} />
      ))}
    </svg>
  );
}

function LogTable({ points, colourOf, unit }) {
  const rows = points.slice().reverse();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-gray-500">
          <tr className="text-left">
            <th className="py-1.5 pr-3 font-normal">Date</th>
            <th className="py-1.5 pr-3 font-normal">Who</th>
            <th className="py-1.5 pr-3 font-normal">Session</th>
            <th className="py-1.5 pr-3 font-normal">Sets (reps × {unit})</th>
            <th className="py-1.5 pr-3 font-normal text-right">Reps</th>
            <th className="py-1.5 pr-3 font-normal text-right">Heaviest</th>
            <th className="py-1.5 pr-3 font-normal text-right">Est. 1RM</th>
            <th className="py-1.5 font-normal text-right">Volume</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(p => (
            <tr key={p.runId} className="border-t border-[#232323]">
              <td className="py-1.5 pr-3 whitespace-nowrap text-gray-300">{day(p.date)}</td>
              <td className="py-1.5 pr-3 whitespace-nowrap text-gray-300"><span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: colourOf[p.userId] }} />{p.who}</td>
              <td className="py-1.5 pr-3 text-gray-400">{p.session}</td>
              <td className="py-1.5 pr-3 tabular-nums text-gray-200">{setsText(p.sets, unit)}</td>
              <td className="py-1.5 pr-3 tabular-nums text-right">{p.reps}</td>
              <td className="py-1.5 pr-3 tabular-nums text-right">{kg(p.top, unit)}</td>
              <td className="py-1.5 pr-3 tabular-nums text-right">{kg(p.e1rm, unit)}</td>
              <td className="py-1.5 tabular-nums text-right">{Math.round(fromKg(p.volume, unit))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
